import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { apiService } from '../../services/api';

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (project: any) => void;
  onError?: (error: string) => void;
  useAdminTasksAPI?: boolean; // If true, uses Admin Tasks API, otherwise uses Tyvra PM API
}

interface ProjectFormData {
  name: string;
  description: string;
  client: string;
  projectManager: string;
  startDate: Date | null;
  expectedEndDate: Date | null;
  budget: number | string;
  hourlyRate: number | string;
  teamSize: number | string;
  status: string;
  // Tyvra PM specific fields
  key: string;
  storyPrefix: string;
  methodology: string;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
  useAdminTasksAPI = true,
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    client: '',
    projectManager: '',
    startDate: new Date(),
    expectedEndDate: new Date(),
    budget: '',
    hourlyRate: '',
    teamSize: '',
    status: 'active',
    // Tyvra PM specific fields
    key: '',
    storyPrefix: '',
    methodology: 'scrum',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleProjectKeyChange = (name: string) => {
    // Auto-generate project key from name
    const key = name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .substring(0, 6);
    
    setFormData(prev => ({ ...prev, key }));
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      client: '',
      projectManager: '',
      startDate: new Date(),
      expectedEndDate: new Date(),
      budget: '',
      hourlyRate: '',
      teamSize: '',
      status: 'active',
      // Tyvra PM specific fields
      key: '',
      storyPrefix: '',
      methodology: 'scrum',
    });
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields based on API type
      if (useAdminTasksAPI) {
        if (!formData.name || !formData.description || !formData.client || !formData.projectManager) {
          setError('Please fill in all required fields');
          return;
        }

        if (!formData.startDate || !formData.expectedEndDate) {
          setError('Please select both start and end dates');
          return;
        }
      } else {
        // Tyvra PM API validation
        if (!formData.name || !formData.key) {
          setError('Project name and key are required');
          return;
        }
      }

      let response;
      if (useAdminTasksAPI) {
        // Create project data in AdminTasks format
        const projectData = {
          name: formData.name,
          description: formData.description,
          client: formData.client,
          startDate: formData.startDate?.toISOString().split('T')[0],
          expectedEndDate: formData.expectedEndDate?.toISOString().split('T')[0],
          budget: parseFloat(formData.budget.toString()) || 0,
          hourlyRate: parseFloat(formData.hourlyRate.toString()) || 80,
          teamSize: parseInt(formData.teamSize.toString()) || 1,
          projectManager: formData.projectManager,
          status: formData.status,
        };

        console.log('📝 Creating Admin Tasks project:', projectData);
        try {
          response = await apiService.createAdminTasksProject(projectData);
          console.log('✅ Admin Tasks project created successfully:', response);
        } catch (apiError) {
          console.error('❌ Error creating Admin Tasks project:', apiError);
          throw apiError;
        }
      } else {
        // Create project data in Tyvra PM format
        const projectData = {
          name: formData.name,
          key: formData.key,
          storyPrefix: formData.storyPrefix,
          description: formData.description,
          methodology: formData.methodology as 'scrum' | 'kanban' | 'hybrid',
        };

        console.log('📝 Creating Tyvra PM project:', projectData);
        response = await apiService.createProject(projectData);
      }

      console.log('✅ Project created via API:', response);

      // Call success callback
      if (onSuccess) {
        onSuccess(response);
      }

      handleClose();
    } catch (error: any) {
      console.error('❌ Failed to create project:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create project';
      setError(errorMessage);
      
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Project</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Name"
                value={formData.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setFormData({ ...formData, name });
                  if (!useAdminTasksAPI && (!formData.key || formData.key === formData.name.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 6))) {
                    handleProjectKeyChange(name);
                  }
                }}
                required
                disabled={loading}
              />
            </Grid>
            
            {/* Tyvra PM specific fields */}
            {!useAdminTasksAPI && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Project Key"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value.toUpperCase() })}
                    required
                    disabled={loading}
                    helperText="Short unique identifier (e.g., PROJ, DEV)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Story Prefix"
                    value={formData.storyPrefix || ''}
                    onChange={(e) => setFormData({ ...formData, storyPrefix: e.target.value.toUpperCase().substring(0, 4) })}
                    disabled={loading}
                    helperText="Short prefix for story numbers (max 4 chars)"
                    inputProps={{ maxLength: 4 }}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required={useAdminTasksAPI}
                disabled={loading}
              />
            </Grid>
            {/* Admin Tasks specific fields */}
            {useAdminTasksAPI && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Client"
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                    required
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Project Manager"
                    value={formData.projectManager}
                    onChange={(e) => setFormData({ ...formData, projectManager: e.target.value })}
                    required
                    disabled={loading}
                  />
                </Grid>
              </>
            )}
            {useAdminTasksAPI && (
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={(date) => setFormData({ ...formData, startDate: date })}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      required: true,
                      disabled: loading 
                    } 
                  }}
                />
              </Grid>
            )}
            {useAdminTasksAPI && (
              <>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Expected End Date"
                    value={formData.expectedEndDate}
                    onChange={(date) => setFormData({ ...formData, expectedEndDate: date })}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true, 
                        required: true,
                        disabled: loading 
                      } 
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Budget ($)"
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hourly Rate ($)"
                    type="number"
                    value={formData.hourlyRate}
                    onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Team Size"
                    type="number"
                    value={formData.teamSize}
                    onChange={(e) => setFormData({ ...formData, teamSize: e.target.value })}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={loading}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="on-hold">On Hold</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            
            {/* Tyvra PM specific methodology field */}
            {!useAdminTasksAPI && (
              <Grid item xs={12}>
                <FormControl fullWidth disabled={loading}>
                  <InputLabel>Methodology</InputLabel>
                  <Select
                    value={formData.methodology}
                    onChange={(e) => setFormData({ ...formData, methodology: e.target.value })}
                    label="Methodology"
                  >
                    <MenuItem value="scrum">Scrum</MenuItem>
                    <MenuItem value="kanban">Kanban</MenuItem>
                    <MenuItem value="hybrid">Hybrid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating...' : 'Create Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectModal;
